/* ==========================================================================
   EKBATAN — theme.js
   Commerce behaviour as small, self-initialising custom elements.
   No dependencies. Each element survives Theme Editor section reloads.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const root = document.documentElement;

  /* ---------- Utilities ---------- */
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const fetchJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(async (r) => {
      const data = await r.json();
      if (!r.ok || data.status) throw data;
      return data;
    });

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents);
    const fmt = (n, decimals, thousands = ',', dec = '.') => {
      if (isNaN(n)) return '0';
      const parts = (n / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts.join(dec) : parts[0];
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(value, 2);
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        default: return fmt(value, 2);
      }
    });
  };

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';
  const trapFocus = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  let lockCount = 0;
  const lockScroll = (on) => {
    lockCount = Math.max(0, lockCount + (on ? 1 : -1));
    document.body.classList.toggle('is-locked', lockCount > 0);
  };

  const toast = (message) => {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(el._t);
    el._t = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => { el.hidden = true; }, 500);
    }, 3200);
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  };

  /* A reusable open/close overlay: drawers, menu, search. */
  class Overlay extends HTMLElement {
    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this._trigger = trigger || document.activeElement;
      this.hidden = false;
      lockScroll(true);
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      this._release = trapFocus(this);
      this._onKey = (e) => { if (e.key === 'Escape') this.close(); };
      document.addEventListener('keydown', this._onKey);
      if (this._trigger && this._trigger.setAttribute) this._trigger.setAttribute('aria-expanded', 'true');
      this.onOpen && this.onOpen();
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      lockScroll(false);
      this._release && this._release();
      document.removeEventListener('keydown', this._onKey);
      if (this._trigger && this._trigger.setAttribute) this._trigger.setAttribute('aria-expanded', 'false');
      setTimeout(() => { if (!this.classList.contains('is-open')) this.hidden = true; }, 900);
      if (this._trigger && this._trigger.focus) this._trigger.focus({ preventScroll: true });
      this.onClose && this.onClose();
    }
  }

  /* ---------- Header ---------- */
  class HeaderBar extends HTMLElement {
    connectedCallback() {
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.lastY = window.scrollY;
      this.measure();
      this._onScroll = () => { if (!this._ticking) { this._ticking = true; requestAnimationFrame(() => this.update()); } };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._ro = new ResizeObserver(() => this.measure());
      this._ro.observe(this);
      this.update();
      this.setupMega();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      this._ro && this._ro.disconnect();
    }
    measure() {
      const h = this.querySelector('.site-header__bar').offsetHeight;
      root.style.setProperty('--header-h', `${h}px`);
      root.style.setProperty('--header-offset', this.classList.contains('is-hidden') ? '0px' : `${h}px`);
    }
    update() {
      this._ticking = false;
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 24);
      if (this.hideOnScroll && !this.querySelector('details[open]')) {
        const goingDown = y > this.lastY && y > 320;
        if (Math.abs(y - this.lastY) > 6) {
          this.classList.toggle('is-hidden', goingDown);
          this.measure();
        }
      }
      this.lastY = y;
    }
    setupMega() {
      const items = [...this.querySelectorAll('details[data-mega]')];
      const closeAll = (except) => items.forEach((d) => { if (d !== except) d.open = false; });
      items.forEach((d) => {
        d.addEventListener('toggle', () => { if (d.open) closeAll(d); this.classList.toggle('is-open', items.some((i) => i.open)); });
        if (window.matchMedia('(hover: hover)').matches) {
          let t;
          d.addEventListener('mouseenter', () => { clearTimeout(t); t = setTimeout(() => { d.open = true; }, 120); });
          d.addEventListener('mouseleave', () => { clearTimeout(t); t = setTimeout(() => { d.open = false; }, 260); });
        }
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) closeAll(); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const open = items.find((d) => d.open);
          if (open) { open.open = false; open.querySelector('summary').focus(); }
        }
      });
    }
  }
  customElements.define('header-bar', HeaderBar);

  class MenuDrawer extends Overlay {
    connectedCallback() {
      this.querySelectorAll('[data-menu-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    }
    onOpen() {
      document.querySelector('header-bar')?.classList.add('is-open');
      setTimeout(() => this.querySelector('.menu-drawer__link, [data-menu-close]')?.focus({ preventScroll: true }), 400);
    }
    onClose() { document.querySelector('header-bar')?.classList.remove('is-open'); }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* ---------- Search ---------- */
  class SearchModal extends Overlay {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.predictive = this.dataset.predictive === 'true' && !!theme.routes.predictiveSearch;
      this.querySelectorAll('[data-search-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      if (this.predictive) this.input.addEventListener('input', debounce(() => this.search(), 260));
    }
    onOpen() { setTimeout(() => this.input.focus(), 350); }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = ''; this.input.setAttribute('aria-expanded', 'false'); return; }
      this.results.classList.add('is-loading');
      this._ctrl && this._ctrl.abort();
      this._ctrl = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=8&section_id=predictive-search`;
        const html = await fetch(url, { signal: this._ctrl.signal }).then((r) => r.text());
        const content = parseHTML(html).querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = content ? content.innerHTML : '';
        this.input.setAttribute('aria-expanded', 'true');
      } catch (e) { if (e.name !== 'AbortError') this.results.innerHTML = ''; }
      this.results.classList.remove('is-loading');
    }
  }
  customElements.define('search-modal', SearchModal);

  /* ---------- Cart ---------- */
  class CartDrawer extends Overlay {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
    }
    onOpen() { setTimeout(() => this.querySelector('.drawer__panel').focus({ preventScroll: true }), 300); }
    renderFromSections(sections) {
      const html = sections && sections[this.dataset.sectionId];
      if (!html) return;
      const fresh = parseHTML(html).querySelector('cart-drawer');
      if (!fresh) return;
      this.querySelector('[data-cart-root]').innerHTML = fresh.querySelector('[data-cart-root]').innerHTML;
      this.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.sections = (this.dataset.sections || '').split(',').filter(Boolean);
      this._onChange = debounce((e) => {
        const input = e.target.closest('[data-line-quantity]');
        if (input) this.update(Number(input.dataset.lineQuantity), Number(input.value));
      }, 350);
      this.addEventListener('change', this._onChange);
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-line-remove]');
        if (!remove) return;
        e.preventDefault();
        remove.closest('.cart-line')?.classList.add('is-removing');
        this.update(Number(remove.dataset.lineRemove), 0);
      });
    }
    async update(line, quantity) {
      this.classList.add('is-busy');
      try {
        const data = await fetchJSON(`${theme.routes.cartChange}.js`, {
          line, quantity, sections: this.sections, sections_url: window.location.pathname
        });
        this.sections.forEach((id) => {
          const html = data.sections && data.sections[id];
          if (!html) return;
          const fresh = parseHTML(html).querySelector('[data-cart-root]');
          if (fresh) this.innerHTML = fresh.innerHTML;
          const drawer = this.closest('cart-drawer');
          if (drawer) drawer.classList.toggle('is-empty', data.item_count === 0);
        });
        updateCartCount(data.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (err) {
        toast((err && (err.description || err.message)) || theme.strings.cartError);
        window.location.reload();
      }
      this.classList.remove('is-busy');
    }
  }
  customElements.define('cart-items', CartItems);

  class CartNote extends HTMLElement {
    connectedCallback() {
      this.addEventListener('input', debounce((e) => {
        fetchJSON(`${theme.routes.cartUpdate}.js`, { note: e.target.value }).catch(() => {});
      }, 500));
    }
  }
  customElements.define('cart-note', CartNote);

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        const min = Number(this.input.min || 0);
        const before = Number(this.input.value) || 0;
        const next = btn.name === 'plus' ? before + 1 : Math.max(min, before - 1);
        if (next === before) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Product form ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    get submitButtons() {
      const own = [...this.form.querySelectorAll('[type="submit"][name="add"], [data-add-button]')];
      const external = this.form.id ? [...document.querySelectorAll(`[type="submit"][form="${this.form.id}"]`)] : [];
      return [...new Set([...own, ...external])];
    }
    async onSubmit(e) {
      const drawer = document.querySelector('cart-drawer');
      if (theme.cartType === 'page' || !drawer) return; /* native form post → cart page */
      e.preventDefault();
      const buttons = this.submitButtons;
      if (buttons.some((b) => b.getAttribute('aria-busy') === 'true')) return;
      buttons.forEach((b) => { b.setAttribute('aria-busy', 'true'); b.classList.add('is-loading'); });
      this.showError(null);

      const body = new FormData(this.form);
      body.append('sections', drawer.dataset.sectionId);
      body.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
        const data = await res.json();
        if (!res.ok || data.status) throw data;
        drawer.renderFromSections(data.sections);
        const cart = await fetch(`${theme.routes.cart}.js`).then((r) => r.json());
        updateCartCount(cart.item_count);
        drawer.open(buttons[0]);
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      } catch (err) {
        const msg = (err && (err.description || err.message)) || theme.strings.cartError;
        this.showError(msg);
        if (this.hasAttribute('data-quick-add')) toast(msg);
      } finally {
        buttons.forEach((b) => { b.removeAttribute('aria-busy'); b.classList.remove('is-loading'); });
      }
    }
    showError(message) {
      const wrap = this.querySelector('[data-error]');
      if (!wrap) return;
      wrap.hidden = !message;
      if (message) wrap.querySelector('[data-error-message]').textContent = message;
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.addEventListener('change', () => this.onChange());
      this.markAvailability();
    }
    get selectedOptions() {
      const groups = [...this.querySelectorAll('fieldset, .variants__option')];
      return groups.map((group) => {
        const checked = group.querySelector('input:checked');
        const select = group.querySelector('select');
        return checked ? checked.value : select ? select.value : null;
      }).filter((v) => v !== null);
    }
    findVariant(options) {
      return this.variants.find((v) => v.options.every((opt, i) => opt === options[i]));
    }
    markAvailability() {
      const selected = this.selectedOptions;
      this.querySelectorAll('fieldset').forEach((group, index) => {
        group.querySelectorAll('input').forEach((input) => {
          const candidate = [...selected];
          candidate[index] = input.value;
          const match = this.variants.find((v) => v.options.every((opt, i) => i === index ? opt === input.value : (i < index ? opt === candidate[i] : true)));
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !match || !match.available);
        });
        const value = group.querySelector('[data-selected-value]');
        const checked = group.querySelector('input:checked');
        if (value && checked) value.textContent = checked.value;
      });
    }
    onChange() {
      const variant = this.findVariant(this.selectedOptions);
      this.markAvailability();
      const section = this.closest('section, [data-product-section]') || document;
      const formId = `product-form-${this.sectionId}`;
      const addButtons = [...document.querySelectorAll(`#${formId} [data-add-button], [form="${formId}"][type="submit"]`)];
      const status = section.querySelector('[data-product-status]');

      if (!variant) {
        addButtons.forEach((b) => { b.disabled = true; const l = b.querySelector('[data-add-label]') || b; l.textContent = theme.strings.unavailable; });
        if (status) status.textContent = theme.strings.unavailable;
        return;
      }

      document.querySelectorAll(`#${formId} [data-variant-id], #payment-terms-${this.sectionId} input[name="id"]`).forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      addButtons.forEach((b) => {
        b.disabled = !variant.available;
        const l = b.querySelector('[data-add-label]') || b;
        l.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      });
      const stickyPrice = document.querySelector('[data-sticky-price]');
      if (stickyPrice && this.dataset.updateUrl === 'true') stickyPrice.textContent = formatMoney(variant.price);
      if (status) status.textContent = `${variant.title} — ${formatMoney(variant.price)}${variant.available ? '' : ` — ${theme.strings.soldOut}`}`;

      if (variant.featured_media) {
        const gallery = section.querySelector('media-gallery');
        gallery && gallery.showMedia(variant.featured_media.id, true);
      }
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.refreshSection(variant, section);
    }
    async refreshSection(variant, section) {
      this._ctrl && this._ctrl.abort();
      this._ctrl = new AbortController();
      try {
        const html = await fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`, { signal: this._ctrl.signal }).then((r) => r.text());
        const doc = parseHTML(html);
        ['[data-price-wrapper]', '[data-pickup]'].forEach((sel) => {
          const fresh = doc.querySelector(sel);
          const current = section.querySelector(sel);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        const freshSticky = doc.querySelector('[data-sticky-price]');
        const sticky = document.querySelector('[data-sticky-price]');
        if (freshSticky && sticky && this.dataset.updateUrl === 'true') sticky.innerHTML = freshSticky.innerHTML;
      } catch (e) { /* keep client-side values */ }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('.gallery__slide')];
      this.counter = this.querySelector('[data-counter-current]');
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.lightbox = this.querySelector('[data-lightbox]');
      if (!this.track) return;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb))));
      this.track.addEventListener('scroll', debounce(() => this.sync(), 60), { passive: true });
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      if (this.index > 0 && this.isCarousel()) this.go(this.index, false);
      this.sync();

      if (this.lightbox) {
        this.addEventListener('click', (e) => {
          const z = e.target.closest('[data-zoom-open]');
          if (z) this.openZoom(Number(z.dataset.zoomOpen));
        });
        this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.lightbox.close());
        this.lightbox.addEventListener('close', () => lockScroll(false));
      }

      if (this.querySelector('[data-shopify-xr]') && window.Shopify && Shopify.loadFeatures) {
        Shopify.loadFeatures([{ name: 'shopify-xr', version: '1.0', onLoad: () => {
          const json = document.getElementById(`ModelJson-${this.dataset.section}`);
          if (json && window.ShopifyXR) { ShopifyXR.addModels(JSON.parse(json.textContent)); ShopifyXR.setupXRElements(); }
        } }, { name: 'model-viewer-ui', version: '1.0' }]);
      }
    }
    isCarousel() { return this.track.scrollWidth > this.track.clientWidth + 4; }
    go(i, smooth = true) {
      if (!this.slides.length) return;
      this.index = (i + this.slides.length) % this.slides.length;
      const slide = this.slides[this.index];
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      this.mark();
    }
    sync() {
      if (!this.isCarousel()) return;
      const x = this.track.scrollLeft;
      let best = 0; let bestD = Infinity;
      this.slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - this.track.offsetLeft - x); if (d < bestD) { bestD = d; best = i; } });
      if (best !== this.index) { this.index = best; this.mark(); }
      else this.mark();
    }
    mark() {
      this.slides.forEach((s, i) => {
        const active = i === this.index;
        s.classList.toggle('is-active', active);
        if (!active) s.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === this.index));
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
    }
    showMedia(mediaId, fromVariant) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i < 0) return;
      if (this.isCarousel()) { this.go(i); return; }
      /* Editorial desktop layout: bring the variant image to the lead position. */
      const slide = this.slides[i];
      if (fromVariant && i !== 0) {
        this.track.prepend(slide);
        this.slides = [...this.querySelectorAll('.gallery__slide')];
      }
      this.index = 0;
      this.mark();
      const top = this.getBoundingClientRect().top;
      if (top < 0) window.scrollTo({ top: window.scrollY + top - 100, behavior: 'smooth' });
    }
    openZoom(index) {
      if (!this.lightbox) return;
      this.lightbox.showModal();
      lockScroll(true);
      const item = this.lightbox.querySelector(`[data-lightbox-item="${index}"]`);
      if (item) requestAnimationFrame(() => item.scrollIntoView({ block: 'start' }));
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- Sticky buy bar ---------- */
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.form);
      const target = form && form.querySelector('[data-add-button]');
      if (!target || !('IntersectionObserver' in window)) return;
      this.hidden = false;
      this._io = new IntersectionObserver(([entry]) => {
        const past = entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', !entry.isIntersecting && past);
      });
      this._io.observe(target);
    }
    disconnectedCallback() { this._io && this._io.disconnect(); }
  }
  customElements.define('sticky-buy', StickyBuy);

  /* ---------- Recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('.product-grid') || !this.dataset.url) return;
      const io = new IntersectionObserver(async ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        try {
          const html = await fetch(this.dataset.url).then((r) => r.text());
          const fresh = parseHTML(html).querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            document.dispatchEvent(new CustomEvent('theme:content', { detail: this }));
          }
        } catch (e) { /* silent */ }
      }, { rootMargin: '0px 0px 600px 0px' });
      io.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Facets (filters / sort / density) ---------- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.drawer = this.querySelector('[data-facets-drawer]');
      this.form = this.querySelector('[data-facets-form]');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-facets-open]')) this.openDrawer(e.target.closest('[data-facets-open]'));
        else if (e.target.closest('[data-facets-close]')) this.closeDrawer();
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); this.closeDrawer(); this.render(new URL(link.href, location.origin).searchParams.toString()); }
        const density = e.target.closest('[data-density]');
        if (density) this.setDensity(density.dataset.density);
      });
      const onFormChange = debounce(() => this.render(this.params()), 450);
      this.form && this.form.addEventListener('input', onFormChange);
      this.form && this.form.addEventListener('submit', (e) => { e.preventDefault(); this.render(this.params()); });
      this.querySelector('[data-facets-sort]')?.addEventListener('change', () => this.render(this.params()));
      let saved = null;
      try { saved = localStorage.getItem('ekbatan:density'); } catch (e) { /* storage unavailable */ }
      if (saved) this.setDensity(saved, false);
      if (!FacetFilters._pop) {
        FacetFilters._pop = true;
        window.addEventListener('popstate', () => {
          const current = document.querySelector('facet-filters');
          current && current.render(location.search.slice(1), false);
        });
      }
    }
    openDrawer(trigger) {
      this._trigger = trigger;
      this.drawer.hidden = false;
      lockScroll(true);
      requestAnimationFrame(() => requestAnimationFrame(() => this.drawer.classList.add('is-open')));
      trigger.setAttribute('aria-expanded', 'true');
      this._release = trapFocus(this.drawer);
      this._onKey = (e) => { if (e.key === 'Escape') this.closeDrawer(); };
      document.addEventListener('keydown', this._onKey);
    }
    closeDrawer() {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.classList.remove('is-open');
      lockScroll(false);
      this._release && this._release();
      document.removeEventListener('keydown', this._onKey);
      this._trigger && this._trigger.setAttribute('aria-expanded', 'false');
      setTimeout(() => { if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true; }, 800);
    }
    params() {
      const data = new FormData(this.form);
      const sort = this.querySelector('[data-facets-sort]');
      if (sort) data.set('sort_by', sort.value);
      const params = new URLSearchParams();
      for (const [k, v] of data.entries()) if (v !== '') params.append(k, v);
      return params.toString();
    }
    setDensity(value, persist = true) {
      const grid = document.querySelector(`[data-results-section="${this.sectionId}"] [data-grid]`);
      if (grid) grid.dataset.density = value;
      this.querySelectorAll('[data-density]').forEach((b) => b.classList.toggle('is-active', b.dataset.density === value));
      if (persist) { try { localStorage.setItem('ekbatan:density', value); } catch (e) { /* noop */ } }
    }
    async render(search, push = true) {
      const section = document.querySelector(`[data-results-section="${this.sectionId}"]`);
      const results = section && section.querySelector('[data-results]');
      results && results.classList.add('is-loading');
      const url = `${location.pathname}?${search}${search ? '&' : ''}section_id=${this.sectionId}`;
      try {
        const html = await fetch(url).then((r) => r.text());
        const doc = parseHTML(html);
        const freshSection = doc.querySelector(`[data-results-section="${this.sectionId}"]`);
        if (!freshSection || !section) { location.search = search; return; }
        const wasOpen = this.drawer && !this.drawer.hidden;
        const openGroups = [...this.querySelectorAll('.facets__group')].map((d) => d.open);
        section.replaceWith(freshSection);
        const facets = freshSection.querySelector('facet-filters');
        if (facets && wasOpen) {
          facets.drawer.hidden = false;
          facets.drawer.classList.add('is-open');
          facets.querySelectorAll('.facets__group').forEach((d, i) => { if (openGroups[i] !== undefined) d.open = openGroups[i]; });
          facets._trigger = facets.querySelector('[data-facets-open]');
          facets._release = trapFocus(facets.drawer);
          facets._onKey = (e) => { if (e.key === 'Escape') facets.closeDrawer(); };
          document.addEventListener('keydown', facets._onKey);
        } else if (wasOpen) {
          lockScroll(false);
        }
        if (push) history.pushState({}, '', `${location.pathname}${search ? `?${search}` : ''}`);
        document.dispatchEvent(new CustomEvent('theme:content', { detail: freshSection }));
      } catch (e) {
        location.search = search;
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ---------- Small editorial behaviours ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.announcement__item')];
      if (this.items.length < 2) return;
      this.i = 0;
      const interval = Number(this.dataset.interval) || 6000;
      const tick = () => {
        this.items[this.i].classList.remove('is-active');
        this.items[this.i].setAttribute('aria-hidden', 'true');
        this.i = (this.i + 1) % this.items.length;
        this.items[this.i].classList.add('is-active');
        this.items[this.i].removeAttribute('aria-hidden');
      };
      this._timer = setInterval(tick, interval);
      this.addEventListener('mouseenter', () => clearInterval(this._timer));
      this.addEventListener('mouseleave', () => { clearInterval(this._timer); this._timer = setInterval(tick, interval); });
    }
    disconnectedCallback() { clearInterval(this._timer); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  class QuoteCarousel extends HTMLElement {
    connectedCallback() {
      this.quotes = [...this.querySelectorAll('.quote')];
      this.dots = [...this.querySelectorAll('[data-quote-dot]')];
      if (this.quotes.length < 2) return;
      this.i = 0;
      this.dots.forEach((d) => d.addEventListener('click', () => { this.show(Number(d.dataset.quoteDot)); this.restart(); }));
      this.restart();
    }
    restart() {
      clearInterval(this._timer);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this._timer = setInterval(() => this.show((this.i + 1) % this.quotes.length), Number(this.dataset.interval) || 8000);
    }
    show(i) {
      this.i = i;
      this.quotes.forEach((q, n) => { q.classList.toggle('is-active', n === i); q.toggleAttribute('aria-hidden', n !== i); });
      this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    }
    disconnectedCallback() { clearInterval(this._timer); }
  }
  customElements.define('quote-carousel', QuoteCarousel);

  class VideoPlayer extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-play]');
      const tpl = this.querySelector('template[data-embed]');
      if (!btn || !tpl) return;
      btn.addEventListener('click', () => {
        btn.replaceWith(tpl.content.cloneNode(true));
        this.querySelector('iframe')?.focus();
      });
    }
  }
  customElements.define('video-player', VideoPlayer);

  class ProductRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      this.bar = this.querySelector('[data-rail-progress]');
      if (!this.track) return;
      const step = () => (this.track.querySelector('.rail__item')?.offsetWidth || 300) + 24;
      this.querySelector('[data-rail-prev]')?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.querySelector('[data-rail-next]')?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      const progress = () => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const visible = this.track.clientWidth / this.track.scrollWidth;
        const p = max > 0 ? this.track.scrollLeft / max : 1;
        this.bar && this.bar.style.setProperty('--progress', Math.max(visible, visible + (1 - visible) * p).toFixed(3));
      };
      this.track.addEventListener('scroll', () => requestAnimationFrame(progress), { passive: true });
      window.addEventListener('resize', debounce(progress, 150));
      progress();
      this.enableDrag();
    }
    enableDrag() {
      if (!window.matchMedia('(pointer: fine)').matches) return;
      let startX = 0; let startLeft = 0; let moved = false; let down = false;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        down = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) { moved = true; this.track.classList.add('is-dragging'); }
        if (moved) this.track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (moved) {
          const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          this.track.addEventListener('click', swallow, { capture: true, once: true });
          setTimeout(() => this.track.removeEventListener('click', swallow, { capture: true }), 50);
        }
        this.track.classList.remove('is-dragging');
      });
    }
  }
  customElements.define('product-rail', ProductRail);

  /* ---------- Product video (full-screen showcase) ----------
     The video plays only while on screen. As the visitor scrolls, --p (0 → 1)
     drives the blur, the veil and the rising glass panel in CSS. */
  const filmsActive = new Set();
  const syncFilmFlag = () => root.classList.toggle('is-film-active', filmsActive.size > 0);

  class ProductFilm extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.playBtn = this.querySelector('[data-film-play]');
      this.soundBtn = this.querySelector('[data-film-sound]');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const saveData = navigator.connection && navigator.connection.saveData;
      this.autoplay = this.dataset.autoplay === 'true' && !reduce && !saveData;
      this.userPaused = false;

      /* Scroll progress through the section, only computed while it is on screen. */
      this._onScroll = () => {
        if (this._queued || !this.visible) return;
        this._queued = true;
        requestAnimationFrame(() => {
          this._queued = false;
          const r = this.getBoundingClientRect();
          const travel = r.height - window.innerHeight;
          const p = travel > 0 ? Math.min(1, Math.max(0, -r.top / travel)) : 0;
          this.style.setProperty('--p', p.toFixed(4));
          this.classList.toggle('is-revealed', p > 0.2);
          if (this.video && this.lowPower && this.autoplay && !this.userPaused) {
            if (p > 0.5 && !this.video.paused) { this._restPaused = true; this.video.pause(); }
            else if (p < 0.42 && this._restPaused) { this._restPaused = false; this.play(); }
          }
        });
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });

      this.querySelectorAll('.showcase__line').forEach((el, i) => el.style.setProperty('--i', i));
      /* On phones and low-power devices, rest the decoder once the video is fully blurred. */
      this.lowPower = this.classList.contains('showcase--full') && (window.innerWidth < 750 || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4));

      this._io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) {
          this._onScroll();
          if (this.video) {
            this.ensureLoaded();
            filmsActive.add(this);
            if (this.autoplay && !this.userPaused) this.play();
            else if (!this.autoplay) this.classList.add('needs-play');
          }
        } else if (this.video) {
          filmsActive.delete(this);
          if (!this.video.paused) this.video.pause();
        }
        syncFilmFlag();
      }, { threshold: 0 });
      this._io.observe(this);

      if (!this.video) return;
      this.video.muted = true;
      this.video.setAttribute('playsinline', '');
      this.video.removeAttribute('controls');
      this.video.removeAttribute('autoplay');
      /* Glass-frame layout: timed text follows the video frame by frame. */
      this.moments = [...this.querySelectorAll('[data-moment-time]')].map((el) => ({ el, t: parseFloat(el.dataset.momentTime) || 0 }));
      this.indexEl = this.querySelector('[data-moment-index]');
      this.fillEl = this.querySelector('[data-film-progress]');
      this.activeMoment = -1;
      if (this.moments.length) this.sync();

      this.video.addEventListener('playing', () => { this.classList.add('is-playing', 'is-ready'); this.classList.remove('needs-play'); this.updatePlayLabel(); this.syncLoop(); });
      this.video.addEventListener('pause', () => { this.classList.remove('is-playing'); this.updatePlayLabel(); });

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-film-bigplay]')) { this.userPaused = false; this.play(); return; }
        if (e.target.closest('[data-film-play]')) {
          if (this.video.paused) { this.userPaused = false; this.play(); } else { this.userPaused = true; this.video.pause(); }
          return;
        }
        if (e.target.closest('[data-film-sound]')) {
          this.video.muted = !this.video.muted;
          this.soundBtn.setAttribute('aria-pressed', String(!this.video.muted));
          this.soundBtn.setAttribute('aria-label', this.video.muted ? this.soundBtn.dataset.labelOff : this.soundBtn.dataset.labelOn);
          this.classList.toggle('is-sound', !this.video.muted);
          if (this.video.paused) { this.userPaused = false; this.play(); }
        }
      });

      this._onVis = () => {
        if (document.hidden && !this.video.paused) this.video.pause();
        else if (!document.hidden && this.visible && this.autoplay && !this.userPaused) this.play();
      };
      document.addEventListener('visibilitychange', this._onVis);
    }
    disconnectedCallback() {
      this._io && this._io.disconnect();
      window.removeEventListener('scroll', this._onScroll);
      document.removeEventListener('visibilitychange', this._onVis);
      filmsActive.delete(this);
      syncFilmFlag();
    }
    ensureLoaded() {
      if (this._loaded) return;
      this._loaded = true;
      this.video.preload = 'auto';
      if (this.video.readyState === 0) this.video.load();
    }
    play() {
      this.ensureLoaded();
      const pr = this.video.play();
      if (pr && pr.catch) pr.catch(() => this.classList.add('needs-play'));
    }
    syncLoop() {
      if (this._syncing || (!this.moments.length && !this.fillEl)) return;
      this._syncing = true;
      const step = () => {
        this.sync();
        if (!this.video.paused && this.visible) {
          if (this.video.requestVideoFrameCallback) this.video.requestVideoFrameCallback(step);
          else requestAnimationFrame(step);
        } else {
          this._syncing = false;
        }
      };
      step();
    }
    sync() {
      const t = this.video.currentTime || 0;
      const d = this.video.duration || 0;
      if (this.fillEl && d) this.fillEl.style.transform = `scaleX(${(t / d).toFixed(4)})`;
      if (!this.moments.length) return;
      let index = 0;
      this.moments.forEach((m, i) => { if (t >= m.t - 0.04) index = i; });
      if (index === this.activeMoment) return;
      this.activeMoment = index;
      this.moments.forEach((m, i) => {
        m.el.classList.toggle('is-active', i === index);
        m.el.classList.toggle('is-past', i < index);
      });
      if (this.indexEl) this.indexEl.textContent = String(index + 1).padStart(2, '0');
    }
    updatePlayLabel() {
      if (!this.playBtn) return;
      this.playBtn.setAttribute('aria-label', this.video.paused ? this.playBtn.dataset.labelPlay : this.playBtn.dataset.labelPause);
    }
  }
  customElements.define('product-film', ProductFilm);

  /* ---------- Global delegated triggers ---------- */
  document.addEventListener('click', (e) => {
    const menu = e.target.closest('[data-menu-open]');
    if (menu) { document.querySelector('menu-drawer')?.open(menu); return; }

    const search = e.target.closest('[data-search-open]');
    if (search) {
      const modal = document.querySelector('search-modal');
      if (modal) { e.preventDefault(); document.querySelector('menu-drawer')?.close(); modal.open(search); }
      return;
    }

    const cart = e.target.closest('[data-cart-open]');
    if (cart) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && theme.cartType === 'drawer') { e.preventDefault(); drawer.open(cart); }
      return;
    }

    const copy = e.target.closest('[data-copy-link]');
    if (copy && navigator.clipboard) {
      navigator.clipboard.writeText(copy.dataset.copyLink).then(() => toast(copy.getAttribute('aria-label') + ' ✓'));
    }
  });

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-autosubmit]')) e.target.form.submit();
  });

  /* Keep the cart drawer honest when the page is restored from bfcache. */
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    fetch(`${theme.routes.cart}.js`).then((r) => r.json()).then((c) => updateCartCount(c.item_count)).catch(() => {});
  });

  window.Ekbatan = { formatMoney, toast, updateCartCount };
})();
